import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import { useSymptomStore } from 'store/symptom';
import SymptomContainer from './components/SymptomContainer';
import { checkSpanishLocale, cn } from 'lib/utils';
import useSymptomChecker from 'hooks/useSymptomChecker';
import { ENGLISH_OTHER, SPANISH_OTHER } from 'lib/constants';
import Checkmark from 'pages/profile/questionnaire/components/Checkmark';
import { Input } from 'components/ui/input';
import { useTranslation } from 'react-i18next';

export default function SymptomFeelSymptoms() {
  const { t } = useTranslation();
  const questionData = useSymptomStore((store) => store.questionData)?.data;

  const isSpanish = checkSpanishLocale();

  const englishChoices = JSON.parse(questionData?.eng_choices || '[]');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '[]');

  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [other, setOther] = useState('');

  const options = isSpanish ? spanishChoices : englishChoices;
  const formattedOptions = options.map((c: unknown) =>
    typeof c === 'string' ? c : Object.keys(c as any)[0]
  );

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const selected =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      let parsedAnswer = [];
      try {
        parsedAnswer = JSON.parse(selected);
      } catch (error) {
        console.log(error);
      }

      const formattedAnswers: string[] = [];
      parsedAnswer.forEach((option: string) => {
        if (typeof option === 'string') {
          formattedAnswers.push(option);
        } else {
          const entry = Object.entries(option);
          if (entry?.length) {
            formattedAnswers.push(entry[0][0]);
            setOther(entry[0][1] as string);
          }
        }
      });

      setSelectedSymptoms(formattedAnswers);
    }
  }, [questionData, isSpanish]);

  const onSelect = (option: string) => {
    let selected = [...selectedSymptoms];
    if (selectedSymptoms.includes(option)) {
      selected = selected.filter((c) => c !== option);
    } else {
      selected.push(option);
    }
    setSelectedSymptoms(selected);
    if (!selected.includes(isSpanish ? SPANISH_OTHER : ENGLISH_OTHER)) {
      setOther('');
    }
  };

  const { onSubmit, isLoading } = useSymptomChecker();

  const onContinue = () => {
    const formattedSelected = selectedSymptoms.includes(
      isSpanish ? SPANISH_OTHER : ENGLISH_OTHER
    )
      ? selectedSymptoms.map((option) => {
          if ([ENGLISH_OTHER, SPANISH_OTHER].includes(option)) {
            return { [option]: other };
          }

          return option;
        })
      : selectedSymptoms;
    const otherLangSymptoms = formattedSelected.map((option) => {
      if (typeof option !== 'string') {
        return { [isSpanish ? ENGLISH_OTHER : SPANISH_OTHER]: other };
      }

      const index = formattedOptions.indexOf(option);
      return isSpanish ? englishChoices[index] : spanishChoices[index];
    });
    const englishOptions = isSpanish ? otherLangSymptoms : formattedSelected;
    const spanishOptions = isSpanish ? formattedSelected : otherLangSymptoms;

    onSubmit({
      eng_choices: JSON.stringify(englishOptions),
      spanish_choices: JSON.stringify(spanishOptions),
      questionData,
    });
  };

  return (
    <SymptomContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={2}
    >
      <div className='grid gap-6 lg:grid-cols-2'>
        {formattedOptions?.map((option: string) => {
          const isSelected = selectedSymptoms.includes(option);
          return (
            <div key={option} className='space-y-2'>
              <motion.div
                onClick={() => onSelect(option)}
                className={cn(
                  'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76] gap-4 transition-all duration-150',
                  isSelected && 'border-primary text-primary bg-primary/10'
                )}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                <p className='text-sm leading-5'>{option}</p>
                <Checkmark isSelected={isSelected} />
              </motion.div>

              {[SPANISH_OTHER, ENGLISH_OTHER].includes(option) &&
                selectedSymptoms.includes(
                  isSpanish ? SPANISH_OTHER : ENGLISH_OTHER
                ) && (
                  <Input
                    placeholder={t('symptom_placeholder')}
                    value={other}
                    onChange={(e) => setOther(e.target.value)}
                    autoFocus
                    className='text-sm'
                  />
                )}
            </div>
          );
        })}
      </div>
    </SymptomContainer>
  );
}
